import React, {useCallback, useEffect, useRef, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import cs from 'classnames';
import debounce from 'lodash/debounce';
import {useSelector} from 'react-redux';

import {TextHighlightsContextProvider} from 'app/comments/TextHighlightsContextProvider';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {useCurrentUser} from 'app/current-user/api';
import {ProcedureEditTour} from 'app/current-user/components/tour/ProcedureEditTour';
import {confirmOnLeaveEnhancer} from 'app/general/confirmOnLeaveEnhancer';
import {extractErrorMessage} from 'app/general/editpage/extract-error-message';
import {showApproveToMakeItLiveAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/ApproveToMakeItLiveAlertBox';
import {DocumentPutToTrashAlertBox} from 'app/general/structures/document-alerts/DocumentPutToTrashAlertBox';
import {DocumentVersionLocked} from 'app/general/structures/document-alerts/DocumentVersionLocked';
import {LargeContentAlertBox} from 'app/general/structures/document-alerts/LargeContentAlertBox';
import {SPPage} from 'app/general/structures/SPPage';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import {useLanesForDocument} from 'app/lanes/api';
import {ClickToChatButton} from 'app/openai/components/ClickToChatButton';
import {EditChatBoxPermissionWrapper} from 'app/openai/components/TextEdition/EditChatBox';
import {cleanStepId} from 'app/openai/utils';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {setSideDiagramState} from 'app/procedures/actions';
import {
  useProcedureByHashid,
  useProcedureVersionById,
  useUpdateProcedure,
  useUpdateProcedureVersion,
} from 'app/procedures/api';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {
  EditableProcedureDiagramMobile,
  EditableProcedureDiagramSidebar,
} from 'app/procedures/components/ProcedureDiagram';
import {ToggleDiagramButton} from 'app/procedures/components/ProcedureDiagram/ToggleDiagramButton';
import {AddNewStepForm} from 'app/procedures/components/ProcedureEditPage/AddNewStepForm';
import {StickySidebar} from 'app/procedures/components/StickySidebar';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {getMemoizedProcedureOrphanSteps} from 'app/procedures/selectors';
import {removeOrphanChoices} from 'app/procedures/utils';
import {OrderableStepsList} from 'app/process/components/NewProcessEditPage/components/OrderableStepsList';
import {ReorderStepsModeTitle} from 'app/process/components/NewProcessEditPage/components/ReorderStepsModeTitle';
import {useAccountSettingsState} from 'app/settings/api';
import {useWidgetIsOpenState} from 'app/settings/hooks';
import {StepsHeightManager} from 'app/steps/components/StepsHeightManager';
import {DocumentShaper, StepShaper} from 'app/steps/document-shaper';
import {scrollIntoNode} from 'app/steps/utils';
import {useNotifications} from 'app/toasts/hooks';
import {appendPathToURL, getObjectAcceptURL} from 'app/urls';
import {INVALID_VERSION_CODE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {isFetchBaseQueryBadRequestCode} from 'app/utils/errors';
import {getProcedureName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {getLocation} from 'app/utils/urls';

import ActiveEditableProcedureStep from './ActiveEditableProcedureStep';
import EditableProcedureMetaData from './EditableProcedureMetaData';
import {EditableProcedureStep} from './EditableProcedureStep';

import type {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import type {ConfirmOnLeaveProps} from 'app/general/confirmOnLeaveEnhancer';
import type {WithShortcutsProps} from 'app/general/shortcuts/withShortcuts';
import type {
  AIDescriptionChangeType,
  AINameChangeType,
  AIStepAddType,
  AIStepContentChangeType,
  AIStepNameChangeType,
  AIStepRemoveType,
  AIStopStreamingType,
} from 'app/openai/types';
import type {DocumentLanesRegistryType} from 'app/procedures/types';
import type {ProcedureContentType} from 'app/tasks/types';
import type {WithRouterProps} from 'react-router';
import type {
  ProcedureStepType,
  ProcedureVersionSerializedType,
} from 'sweet-types';

type LocationQueryType = {
  page: string;
  gid?: string;
  threadid?: string;
};
type RouteParamsType = {
  versionid: string;
  hashid: string;
};
type SaveStatusType = 'editing' | 'saved' | 'saving' | 'error';
type DeletableProcedureStepType = ProcedureStepType & {delete?: boolean};
type EditingContentType = Pick<
  ProcedureContentType,
  'name' | 'description' | 'lanes' | 'thumbnail'
> & {
  content: Array<DeletableProcedureStepType>;
};
type EditingDocWithStepsType = {
  content: Array<DeletableProcedureStepType>;
  lanes: DocumentLanesRegistryType;
};

type RouterProps = WithRouterProps<RouteParamsType, LocationQueryType>;
export type WithShortcutOwnProps = RouterProps & WithShortcutsProps;
type Props = WithShortcutOwnProps & ConfirmOnLeaveProps;

const ProcedureEditPage = ({
  router,
  params,
  location,
  enableShortcut,
  shouldConfirmOnLeave,
  withLeaveConfirmationDisabled,
}: Props) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatusType>('saved');
  const [flush, setFlush] = useState<boolean>(false);
  const [busy, setBusy] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [showOverlayDiagram, setShowOverlayDiagram] = useState<boolean>(false);
  const {versionid, hashid} = params;
  const [
    updateVersion,
    {data: updatedVersion, isLoading: isUpdating, isError, error},
  ] = useUpdateProcedureVersion<{
    data: ProcedureVersionSerializedType;
    isLoading: boolean;
    isError: boolean;
    error: {status: number; data: Array<string>};
  }>();
  const [updateProcedure] = useUpdateProcedure();
  const [inReorderMode, setInReorderMode] = useState<boolean>(false);
  const [show400Message, setShow400Message] = useState<boolean>(false);
  const [show404Message, setShow404Message] = useState<boolean>(false);

  const {data: user} = useCurrentUser();
  const {data: settings} = useAccountSettingsState();
  const {data: procedure, refetch: refetchProcedure} =
    useProcedureByHashid(hashid);
  const {data: version, refetch: refetchVersion} = useProcedureVersionById({
    versionId: versionid,
  });
  const {showToastNotification} = useNotifications();

  const actions = useAsyncActions({setSideDiagramState});

  // prefetch for child components.
  useLanesForDocument(
    version != null
      ? {object_ids: version.id, content_type: 'procedureversion'}
      : skipToken
  );

  const [pendingContent, setPendingContent] = useState<
    EditingContentType | undefined
  >();

  const currentVersionURLRef = useRef<string | undefined>(undefined);
  const isUpdatingRef = useRef<boolean>(false);

  const pendingContentRef = useRef<EditingContentType | undefined>(undefined);

  const canEditTags: boolean = settings?.can_edit_tags === true;

  const closedSideDiagram = useSelector((state) =>
    state.proceduresPage.getIn(
      ['closedSideDiagrams', procedure?.id.toString()],
      !user?.is_diagrams_expanded
    )
  );

  const isShowingChat = useWidgetIsOpenState({
    contentType: procedure?.content_type,
    objectId: procedure?.id,
    widgetName: 'sweetai-chat',
  });
  const {require_ai_data_sharing_at} = useAccountSettingsState().data!;

  const setIsSaving = (value: SaveStatusType) => {
    setSaveStatus(value);
    shouldConfirmOnLeave('procedureEditPage', value !== 'saved');
    return value;
  };

  const thereAreBusySteps = (): boolean => {
    return Object.keys(busy).filter((k) => busy[k] > 0).length > 0;
  };

  useEffect(
    () =>
      enableShortcut('s', (e: any) => {
        if (!thereAreBusySteps() && pendingContent) {
          e.preventDefault();
          addNewStep({title: '', content: pendingContent});
        }
      }),
    []
  );

  useEffect(() => {
    if (isUpdating) {
      setIsSaving('saving');
    } else if (isError) {
      handleUpdateProcedureError(error);
    } else if (!pendingContentRef.current) {
      // Only set 'saved' if there are no pending changes.
      setIsSaving('saved');
    }
  }, [isUpdating, isError]);

  useEffect(() => {
    if (saveStatus === 'editing' && pendingContent != null) {
      // we need to push the content up.
      pendingContentRef.current = pendingContent;
      debouncedPushContent();
    }
  }, [pendingContent]);

  useEffect(() => {
    if (flush) {
      setFlush(false);
      debouncedPushContent.flush();
    }
  }, [flush]);

  useEffect(() => {
    // set initial value of pendingContent once at startup.
    if (pendingContent == null && version != null) {
      setPendingContent({
        name: version.name,
        content: version.content,
        description: version.description,
        lanes: version.lanes,
        thumbnail: version.thumbnail ?? '',
      });
    }
  }, [version?.content]);

  useEffect(() => {
    currentVersionURLRef.current = updatedVersion?.url ?? version?.url;
  }, [version?.id, updatedVersion?.id]);

  useEffect(
    function () {
      isUpdatingRef.current = isUpdating;
    },
    [isUpdating]
  );

  useEffect(() => {
    const versionId = params.versionid;

    if (
      versionId &&
      updatedVersion &&
      parseInt(versionId) !== updatedVersion.id
    ) {
      // update to the latest version.
      redirectToMostRecentURL(updatedVersion.html_url);
    } else if (!isUpdating && !pendingContentRef.current) {
      setIsSaving('saved');
    }
    setShow400Message(false);
  }, [params.versionid, updatedVersion?.id]);

  function pushContent() {
    if (isUpdatingRef.current || !pendingContentRef.current) {
      // don't push whilst the old push hasn't resolved yet.
      return Promise.resolve(false);
    }

    // Clear the pending changes after processing
    const content = pendingContentRef.current;

    pendingContentRef.current = undefined;
    return updateVersion({
      url: currentVersionURLRef.current!,
      ...content,
    })
      .unwrap()
      .catch(() => {
        setSaveStatus('error');
      })
      .finally(function () {
        if (pendingContentRef.current != null) {
          // changes have come in whilst we were POST-ing. Re-queue the job.
          debouncedPushContent();
        }
      });
  }
  const debouncedPushContent = useCallback(debounce(pushContent, 3000), []);

  useEffect(() => {
    Object.entries(busy).forEach(([id, busyCounter]) => {
      shouldConfirmOnLeave(`step-busy-${id}`, busyCounter > 0);
    });
  }, [busy]);

  if (!procedure || !version || !user || !settings || !pendingContent) {
    return null;
  }

  const onAICommands = (
    commands: Array<
      | (AIStepAddType & {command: 'add_actions'})
      | (AIStepRemoveType & {command: 'remove_actions'})
      | (AINameChangeType & {command: 'actions'})
      | (AIDescriptionChangeType & {command: 'actions'})
      | (AIStopStreamingType & {command: 'stop'})
      | ((AIStepNameChangeType | AIStepContentChangeType) & {
          command: 'step_actions';
        })
    >
  ) => {
    let content: EditingContentType = {
      name: version.name,
      content: version.content,
      description: version.description,
      lanes: version.lanes,
      thumbnail: version.thumbnail ?? '',
    };
    let mostRecentAddedStepId: null | string = null;
    commands.forEach((command) => {
      switch (command.command) {
        case 'add_actions': {
          const {
            name: title,
            insert_after_id: stepid,
            content: stepContent,
          } = command;
          const id = cleanStepId(stepid);
          const after =
            stepid === 'most_recent_added' ? mostRecentAddedStepId ?? id : id;
          const {shaper, changes} = addNewStep({
            after,
            title,
            stepContent,
            content,
            isOpen: false,
            immediate: false,
          });
          content = {...content, ...changes};
          mostRecentAddedStepId = shaper.mostRecentAddedStepId;
          break;
        }
        case 'remove_actions': {
          const {id: stepid} = command;
          const {changes} = handleRemoveStep({
            id: stepid,
            content,
            immediate: false,
          });
          content = {...content, ...changes};
          break;
        }
        case 'step_actions': {
          if ('name' in command) {
            const {name: text, id: stepid} = command;
            const updateStepId =
              stepid === 'most_recent_added'
                ? mostRecentAddedStepId ?? stepid
                : stepid;
            const {changes} = handleEditStep({
              text,
              field: 'title',
              id: updateStepId,
              content,
              immediate: false,
            });
            content = {
              ...content,
              ...changes,
            };
          } else if ('content' in command) {
            const {content: text, id: stepid} = command;
            const updateStepId =
              stepid === 'most_recent_added'
                ? mostRecentAddedStepId ?? stepid
                : stepid;
            const {changes} = handleEditStep({
              text,
              field: 'content',
              id: updateStepId,
              content,
              immediate: false,
            });
            content = {
              ...content,
              ...changes,
            };
          }
          break;
        }
        case 'actions': {
          if ('name' in command) {
            const {name: text} = command;
            content = {
              ...content,
              name: text,
            };
          } else if ('description' in command) {
            const {description: text} = command;
            const description: EditingContentType['description'] = {
              ...content.description,
              content: text,
            };
            content = {
              ...content,
              description,
            };
          }
          break;
        }
        case 'stop': {
          setFlush(true);
        }
      }
    });
    if (commands.filter((command) => command.command !== 'stop').length > 0) {
      // don't push any content if nothing changed i would say.
      doPushContent(content, false);
    }
  };

  const orphans = getMemoizedProcedureOrphanSteps(
    version.content,
    procedure.steps_blocked_at
  );

  const mainColClass = cs('pt-4', 'col-12', {
    'col-md-8': !(
      closedSideDiagram && !(isShowingChat && require_ai_data_sharing_at)
    ),
  });
  const addNewStep = ({
    type = 'content',
    after,
    content,
    title = '',
    stepContent = '',
    isOpen = true,
    immediate = true,
  }: {
    type?:
      | 'content'
      | 'decision'
      | 'automation'
      | 'ending'
      | 'processprocedure';
    after?: string;
    title?: string;
    stepContent?: string;
    content: EditingContentType;
    isOpen?: boolean;
    immediate?: boolean;
  }): {shaper: DocumentShaper; changes: EditingDocWithStepsType} =>
    applyShaper({
      mutate: (shaper, setEdit) =>
        (type === 'content'
          ? shaper.createContentStep({
              after,
              title,
              content: {
                content: stepContent,
              },
            })
          : type === 'decision'
          ? shaper.createDecisionStep({
              after,
              title,
              content: {
                content: stepContent,
              },
            })
          : type === 'automation'
          ? shaper.createAutomationStep({
              after,
              title,
              content: {
                content: stepContent,
              },
            })
          : shaper.createEndingStep({
              after,
              title,
              content: {
                content: stepContent,
              },
            })
        )
          .runSideEffect((stepShaper) =>
            isOpen ? setEdit(stepShaper.getId()) : null
          )
          .procedure(),
      immediate,
      content: content,
    });

  const saveAndRunFunc = (callback?: () => Promise<unknown>) => {
    setFlush(false);
    setEditing({});
    debouncedPushContent.cancel();
    let promise: Promise<unknown> = Promise.resolve(saveStatus);
    if (saveStatus !== 'saved') {
      promise = pushContent().catch(() => {
        setIsSaving('error');
        throw new Error('Error saving procedure');
      });
    }

    if (callback) {
      promise = promise.then((status) => status == 'saved' && callback());
    }

    return promise;
  };

  const saveAndViewAcceptance = (callback) =>
    saveAndRunFunc(callback).then(() =>
      router.push(
        getObjectAcceptURL(procedure, version.id, location.search || '')
      )
    );

  const saveAndView = () =>
    saveAndRunFunc().then(() =>
      router.push(getLocation(version.html_url + (location.search || '')))
    );

  const setStepBusyStatus = (id: string, isBusy: boolean) => {
    setBusy((busy) => {
      const busyCounter = Math.max(0, (busy[id] || 0) + (isBusy ? 1 : -1));
      return {...busy, [id]: busyCounter};
    });
  };
  const toggleDiagramOverlay = () => {
    setShowOverlayDiagram((showOverlayDiagram) => !showOverlayDiagram);
  };
  const toggleSidebarDiagram = () =>
    actions.setSideDiagramState(procedure.id, !closedSideDiagram);
  const handleDiagramStepClick = (stepId: string) => {
    handleSetStepEditing(stepId, true, true, () => scrollIntoNode(stepId, 100));
  };
  const reorderSteps = (sourceId: string, destId: string) => {
    applyShaper({
      mutate: (shaper) => shaper.moveStepById(sourceId, destId),
      immediate: true,
      content: pendingContent,
    });
  };
  const toggleReorderMode = () => {
    return setInReorderMode((inReorderMode) => !inReorderMode);
  };
  const doPushContent = (
    content: Partial<EditingContentType>,
    immediate = false
  ) => {
    setIsSaving('editing');
    // @ts-expect-error typescript hates partial overwriting non-partial due to undefined.
    setPendingContent((old) => ({...old, ...content}));
    if (immediate) {
      setFlush(true);
    }
  };

  const applyShaper = ({
    mutate,
    immediate,
    content,
  }: {
    mutate: (
      shaper: DocumentShaper,
      setEdit: (id: string | false) => any
    ) => DocumentShaper;
    immediate: boolean;
    content: EditingContentType;
  }): {shaper: DocumentShaper; changes: EditingDocWithStepsType} => {
    const shaper = new DocumentShaper(content);
    // @ts-expect-error this is erroring out because of processprocedure.
    const changes: EditingDocWithStepsType = mutate(
      shaper,
      (stepId: string | false) => {
        if (stepId === false) {
          setEditing({});
        } else {
          setEditing({[stepId]: true});
          setInReorderMode(false);
        }
      }
    ).val();
    doPushContent(changes, immediate);
    return {shaper, changes};
  };
  const createApplyStepShaper =
    (stepId: string) =>
    (
      mutate: (
        shaper: StepShaper,
        setEdit: (id: string | false) => any
      ) => DocumentShaper,
      immediate?: boolean
    ) =>
      applyShaper({
        mutate: (shaper, setEdit) => mutate(shaper.step(stepId), setEdit),
        immediate: immediate ?? false,
        content: pendingContent,
      });

  const reload = () => {
    refetchProcedure();
    refetchVersion();
  };

  const disableIsSavingAndReload = () => {
    setIsSaving('saved');
    reload();
  };

  const setEditingMeta = (editing: boolean) => {
    handleSetStepEditing('meta', editing, editing);
  };
  const handleSetStepEditing = (
    id: string,
    isEditing: boolean,
    clearAll = false,
    callback?: () => any
  ) => {
    if (thereAreBusySteps()) {
      return;
    }

    const newEditing = {
      ...(clearAll ? Object.freeze({}) : editing),
      [id]: isEditing,
    };
    setEditing(newEditing);
    if (callback) {
      callback();
    }
  };
  const handleRemoveStep = ({
    id,
    content,
    immediate = true,
  }: {
    id: string;
    content: EditingContentType;
    immediate: boolean;
  }) => {
    const stepId = cleanStepId(id);
    if (immediate) {
      shouldConfirmOnLeave(`step-busy-${stepId}`, false);
    }
    return applyShaper({
      mutate: (shaper, setEdit) =>
        shaper
          .step(stepId)
          .remove()
          .runSideEffect(() => setEdit(false)),
      immediate,
      content,
    });
  };
  const handleCloneStep = (id: string) =>
    applyShaper({
      mutate: (stepShaper, setEdit) =>
        stepShaper
          .step(cleanStepId(id))
          .clone()
          .runSideEffect((clonedStepShaper) =>
            setEdit(clonedStepShaper.getId())
          )
          .procedure(),
      immediate: false,
      content: pendingContent,
    });

  const handleEditStep = ({
    text,
    field,
    id,
    content,
    immediate = true,
  }: {
    text: string;
    field: 'title' | 'content';
    id: string;
    content: EditingContentType;
    immediate?: boolean;
  }): {shaper: DocumentShaper; changes: EditingDocWithStepsType} =>
    applyShaper({
      mutate: (shaper) =>
        shaper
          .step(cleanStepId(id))
          .update({[field]: text})
          .procedure(),
      immediate,
      content: content,
    });

  const handleConvertToEnding = (id: string) =>
    applyShaper({
      mutate: (stepShaper) =>
        stepShaper.step(cleanStepId(id)).convertToEndingType().procedure(),
      immediate: false,
      content: pendingContent,
    });
  const handleStepDataUpdate = (newData: any, id: string) =>
    applyShaper({
      mutate: (shaper) =>
        shaper.step(cleanStepId(id)).update(newData).procedure(),
      immediate: false,
      content: pendingContent,
    });

  const handleFinishedEditingStep = (id: string) => {
    handleSetStepEditing(cleanStepId(id), false);

    if (saveStatus !== 'saved') {
      doPushContent({}, true);
    }
  };

  const handleConvertToNormal = (id: string) =>
    applyShaper({
      mutate: (stepShaper) =>
        stepShaper.step(cleanStepId(id)).convertToContentType().procedure(),
      immediate: true,
      content: pendingContent,
    });

  const handleConvertToDecision = (id: string) =>
    applyShaper({
      mutate: (stepShaper) =>
        stepShaper.step(cleanStepId(id)).convertToDecisionType().procedure(),
      immediate: false,
      content: pendingContent,
    });

  const setReviewDate = (date: string | null) => {
    setIsSaving('saving');
    return updateProcedure({
      url: procedure.url,
      next_review_at: date,
    });
  };
  const handleTagChange = (promise: Promise<any>) => {
    setIsSaving('saving');
    return promise.finally(disableIsSavingAndReload);
  };

  const redirectToMostRecentURL = (html_url) => {
    setShow400Message(false);
    let editUrl = 'edit/';
    const processHashid = location.query.gid;

    if (processHashid != null) {
      editUrl += '?gid=' + processHashid;
    }

    withLeaveConfirmationDisabled(() =>
      router.replace(getLocation(appendPathToURL(html_url, editUrl)))
    );
  };

  function handleUpdateProcedureError(error: FetchBaseQueryError) {
    const ct = getContentType('procedure').name;

    if (error.status === 404) {
      showToastNotification(
        user?.is_super_manager
          ? gt('This %(ct)s was deleted', {
              ct,
            })
          : gt('You no longer have access to this %(ct)s', {
              ct,
            }),
        {level: 'error'}
      );
      setShow404Message(true);
    }

    if (error.status === 429) {
      showToastNotification(
        gt(
          'Daily limit for for AI requests exceeded.' +
            ' Contact %(email)s if you want to use this feature more.',
          {
            email: (
              <a className="text-nowrap" href="mailto:support@sweetprocess.com">
                <i className="far fa-envelope mr-1" />
                support@sweetprocess.com
              </a>
            ),
          }
        )
      );
    }

    if (error.status === 400) {
      const errorMessage = extractErrorMessage(error.data);

      if (errorMessage) {
        showToastNotification(gt('Error saving procedure: %s', errorMessage), {
          level: 'error',
          timeout: 8000,
        });
      } else if (
        isFetchBaseQueryBadRequestCode(error) &&
        error.data.code === INVALID_VERSION_CODE
      ) {
        showToastNotification(
          gt(
            'This draft document is now old. Please make updates to the most recent version instead.'
          ),
          {
            level: 'error',
            timeout: 3000,
            link: {
              text: gt('Edit most recent version'),
              to: procedure?.html_url + 'edit/',
            },
          }
        );
        setShow400Message(true);
      } else {
        showToastNotification(gt('Error saving procedure.'), {
          level: 'error',
          timeout: 8000,
        });
      }
    }

    setIsSaving('error');
  }

  return (
    <StepsHeightManager>
      {({lockStepHeight, unlockStepHeight, getStepLiRef}) => (
        <CollapsedStepsState
          stepsIds={pendingContent.content.map(({id}) => id)}
        >
          {({
            numCollapsedSteps,
            toggleAllStepsCollapse,
            collapsedSteps,
            toggleStepCollapse,
          }) => (
            <SPPage
              pageTitle={gt('%(name)s | Editing', {
                name: version.name,
              })}
              horizontalPadding={false}
              breadcrumbProps={{
                procedureId: procedure.id,
                docName: getProcedureName(procedure.name),
                link: procedure.html_url + location.search,
              }}
              actionArea={
                <GenericDocumentActionButtons
                  pageContext="edit"
                  contentType="procedure"
                  version={version}
                  saveAndView={saveAndView}
                  document={procedure}
                  updateVersion={() => {
                    doPushContent({}, true);
                    return Promise.resolve(true);
                  }}
                  status={saveStatus}
                  canChange={procedure.can_change}
                  onApprove={saveAndRunFunc}
                  onApprovalRequest={saveAndViewAcceptance}
                />
              }
              alwaysVisibleContent={
                <ToggleDiagramButton
                  breakpoint="md"
                  showSideDiagramButton={closedSideDiagram}
                  isOverlayDiagramOpen={showOverlayDiagram}
                  toggleDiagramSidebar={toggleSidebarDiagram}
                  toggleDiagramOverlay={toggleDiagramOverlay}
                />
              }
              alternativeOverlayContent={
                showOverlayDiagram ? (
                  <EditableProcedureDiagramMobile
                    onApplyDocumentShaper={(mutate, immediate) =>
                      applyShaper({mutate, immediate, content: pendingContent})
                    }
                    toggleDiagram={toggleDiagramOverlay}
                    steps={removeOrphanChoices(pendingContent.content)}
                  />
                ) : null
              }
            >
              <DocumentPresenceMembers
                contentType="procedure"
                objectId={procedure.id}
                containerClass={cs('col-12', {
                  'col-md-8': !closedSideDiagram,
                })}
                pageContext="edit"
                contexts={['view', 'edit', 'task']}
              />

              <VersionInfoBar
                objectTypeName={gt('procedure')}
                version={version}
              />

              {show400Message && (
                <DocumentVersionLocked
                  contentType="procedure"
                  editDocumentUrl={procedure.html_url + 'edit/'}
                />
              )}
              <VideoManagerProvider>
                <TextHighlightsContextProvider contentType={'procedure'}>
                  <EditableProcedureMetaData
                    editing={editing.meta}
                    versionId={version.id}
                    isBusy={busy.meta > 0}
                    setEditing={setEditingMeta}
                    onChangeBusyStatus={setStepBusyStatus}
                    procedure={procedure}
                    content={{
                      name: pendingContent.name,
                      description: pendingContent.description,
                      thumbnail: pendingContent.thumbnail,
                    }}
                    initialThumbnail={version.thumbnail}
                    onTagsChange={handleTagChange}
                    setReviewDate={setReviewDate}
                    warningComponent={
                      <div>
                        {showApproveToMakeItLiveAlertBoxIfNotApproved(
                          'procedure',
                          procedure.approved_at
                        )}
                        <LargeContentAlertBox
                          contentType="procedureversion"
                          id={procedure.id}
                          isLarge={version.is_large}
                        />
                        {show404Message && (
                          <DocumentPutToTrashAlertBox
                            contentType="procedure"
                            isSuperManager={user.is_super_manager}
                          />
                        )}
                      </div>
                    }
                    updateContent={doPushContent}
                    canCreateTags={procedure.can_edit}
                    canEditTags={canEditTags || procedure.can_edit}
                    contentType={'procedureversion'}
                    objectId={version.id}
                    editableEmptyDataMessage={
                      procedure.can_edit
                        ? gt('Click to add tags or description...')
                        : gt('Click to add a description...')
                    }
                    editMode={true}
                  />
                </TextHighlightsContextProvider>

                <StyledStepsListDIV>
                  <div className="row">
                    <div className={mainColClass}>
                      {inReorderMode ? (
                        <OrderableStepsList
                          content={pendingContent.content.filter(
                            (step) => step.delete !== true
                          )}
                          onReorder={reorderSteps}
                          toggleReorderMode={toggleReorderMode}
                        />
                      ) : (
                        <>
                          <ReorderStepsModeTitle
                            onToggleReorderMode={toggleReorderMode}
                            numCollapsedSteps={numCollapsedSteps}
                            toggleAllStepsCollapse={toggleAllStepsCollapse}
                            numSteps={
                              pendingContent.content.filter(
                                (step) => step.delete !== true
                              ).length
                            }
                          />
                          <ol className="steps-list-tour steps">
                            {pendingContent.content
                              .filter((step) => step.delete !== true)
                              .map((step) => {
                                return (
                                  <li
                                    id={step.id}
                                    key={step.id}
                                    ref={getStepLiRef(step.id)}
                                  >
                                    {editing[step.id] === true ? (
                                      <ActiveEditableProcedureStep
                                        url={currentVersionURLRef.current!}
                                        step={step}
                                        stepLaneId={
                                          pendingContent.lanes[step.id]
                                        }
                                        steps={pendingContent.content}
                                        contentType={'procedureversion'}
                                        richTextContentType={'procedureversion'}
                                        richTextObjectId={version.id}
                                        applyStepShaper={createApplyStepShaper(
                                          step.id
                                        )}
                                        isBusy={busy[step.id] > 0}
                                        onStepDataUpdate={handleStepDataUpdate}
                                        onRemoveStep={(id) =>
                                          handleRemoveStep({
                                            id,
                                            content: pendingContent,
                                            immediate: true,
                                          })
                                        }
                                        addNewStep={({after, type}) =>
                                          addNewStep({
                                            after,
                                            type,
                                            content: pendingContent,
                                          })
                                        }
                                        onToggleReorderMode={toggleReorderMode}
                                        mediaURLRead={settings.MEDIA_URL_READ}
                                        onFinishEditing={
                                          handleFinishedEditingStep
                                        }
                                        onChangeBusyStatus={setStepBusyStatus}
                                        onDisableEditing={() =>
                                          handleSetStepEditing(step.id, false)
                                        }
                                        onMount={() =>
                                          unlockStepHeight(step.id)
                                        }
                                      />
                                    ) : (
                                      <EditableProcedureStep
                                        orphan={orphans.has(step.id)}
                                        AIEditButton={
                                          step.type !== 'decision' ? (
                                            <ClickToChatButton
                                              text={step.content}
                                              title={step.title}
                                              onConfirmText={(text) =>
                                                handleStepDataUpdate(
                                                  {
                                                    content: text,
                                                  },
                                                  step.id
                                                )
                                              }
                                              document={version}
                                              context={{
                                                step: step.id,
                                              }}
                                            />
                                          ) : null
                                        }
                                        setStepEditing={(...args) => {
                                          lockStepHeight(step.id);
                                          handleSetStepEditing(...args);
                                        }}
                                        steps={pendingContent.content}
                                        collapsed={collapsedSteps[step.id]}
                                        onToggleCollapse={toggleStepCollapse}
                                        onToggleReorderMode={toggleReorderMode}
                                        step={step}
                                        onRemoveStep={(id: string) =>
                                          handleRemoveStep({
                                            id,
                                            content: pendingContent,
                                            immediate: true,
                                          })
                                        }
                                        stepLaneId={
                                          pendingContent.lanes[step.id]
                                        }
                                        onCloneStep={handleCloneStep}
                                        onConvertToEnding={
                                          handleConvertToEnding
                                        }
                                        onConvertToNormal={
                                          handleConvertToNormal
                                        }
                                        onConvertToDecision={
                                          handleConvertToDecision
                                        }
                                      />
                                    )}
                                  </li>
                                );
                              })}
                          </ol>
                        </>
                      )}
                      <AddNewStepForm
                        title={gt('Add a step...')}
                        addNewStep={(type) =>
                          addNewStep({
                            type,
                            content: pendingContent,
                          })
                        }
                        disabled={false}
                      />
                    </div>
                    <div className="col-md-4 mt-4">
                      <StickySidebar
                        height="100vh"
                        className="col-12 bg-white d-print-none"
                      >
                        <>
                          <EditChatBoxPermissionWrapper
                            title={getProcedureName(version.name)}
                            document={version}
                            onCommands={onAICommands}
                            chatType="edit_procedure_chat"
                            initialThreadId={location.query.threadid}
                          />
                          {!showOverlayDiagram && !closedSideDiagram && (
                            <EditableProcedureDiagramSidebar
                              onStepClick={handleDiagramStepClick}
                              versionId={version.id}
                              versionContentType="procedureversion"
                              onApplyDocumentShaper={(mutate, immediate) =>
                                applyShaper({
                                  mutate,
                                  immediate: immediate ?? false,
                                  content: pendingContent,
                                })
                              }
                              toggleSidebarDiagram={toggleSidebarDiagram}
                              toggleOverlayDiagram={toggleDiagramOverlay}
                              steps={removeOrphanChoices(
                                pendingContent.content.filter(
                                  (step) => step.delete !== true
                                )
                              )}
                            />
                          )}
                        </>
                      </StickySidebar>
                    </div>
                  </div>
                </StyledStepsListDIV>
              </VideoManagerProvider>
              <ProcedureEditTour />
            </SPPage>
          )}
        </CollapsedStepsState>
      )}
    </StepsHeightManager>
  );
};

export default confirmOnLeaveEnhancer(() =>
  gt('Your changes are still being saved. Are you sure you want to leave?')
)(ProcedureEditPage);
